import { Injectable, inject } from '@angular/core';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { HttpHeaderConstants } from '../http-header.constant';
import { SpinnerService } from '../spinner';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AppInstanceSchedule } from '../../models/appcloud/schedule.model';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ScheduleService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private APPINSTANCE_URL: string = environment.publicApiHost + '/appcloud/appinstances';

    schedule(
        appinstanceId: number,
        appinstanceSchedule: AppInstanceSchedule,
        additionalParams: any = {},
        additionalHeaders: any = {}
    ): Observable<any> {
        this.#spinnerService.addProcess(
            'ScheduleService.schedule.' + appinstanceId,
            'Scheduling your app configuration...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };

        if (additionalParams) {
            httpOptions.params = new HttpParams();
            Object.entries(additionalParams).forEach(([key, value]) => httpOptions.params.set(key, String(value)));
        }

        return this.#http.put<Observable<any>>(this.buildUrl(appinstanceId), appinstanceSchedule, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while updating your configuration schedule. Please try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ScheduleService.schedule.' + appinstanceId))
        );
    }

    scheduleMultiple(
        appinstanceId: number,
        appinstanceSchedules: AppInstanceSchedule[],
        additionalParams: any = {},
        additionalHeaders: any = {}
    ): Observable<any> {
        this.#spinnerService.addProcess(
            'ScheduleService.scheduleMultiple.' + appinstanceId,
            'Scheduling your app configuration...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };

        if (additionalParams) {
            httpOptions.params = new HttpParams();
            Object.entries(additionalParams).forEach(([key, value]) => httpOptions.params.set(key, String(value)));
        }

        return this.#http.put<Observable<any>>(this.buildUrl(appinstanceId), appinstanceSchedules, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while updating your configuration schedule(s). Please try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ScheduleService.scheduleMultiple.' + appinstanceId))
        );
    }

    delete(
        appinstanceId: number,
        jobGroup: string,
        additionalHeaders: any = {},
        additionalNamingParameters?: string[]
    ): Observable<any> {
        this.#spinnerService.addProcess(
            'ScheduleService.delete.' + appinstanceId,
            'Disabling your app configuration schedule...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('jobGroup', jobGroup),
        };

        if (additionalNamingParameters) {
            additionalNamingParameters.forEach((param, i) => {
                httpOptions.params = httpOptions.params.set('additionalNamingParameters', param);
            });
        }

        return this.#http.delete<Observable<any>>(this.buildUrl(appinstanceId), httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while disabling your schedule. Please try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ScheduleService.delete.' + appinstanceId))
        );
    }

    private buildUrl(appinstanceId: number): string {
        return `${this.APPINSTANCE_URL}/${appinstanceId}/schedules`;
    }
}
